'use client';

import styled from '@emotion/styled';
import { ISecurityCheckpoint } from '../domain/models/ISecurityCheckpoint';
import SecurityWaitTimesWidgetShimmer from './SecurityWaitTimesWidgetShimmer';
import { ChevronRightIcon, Css, Flex, Responsive } from '../../../ui';
import {
	CheckpointLabel,
	Container,
	MainInformationWrapper,
	MoreInfoButton,
	MoreInfoLink,
	SWTCardSquircleIcon,
} from './SecurityWaitTimesWidget.styles';
import {
	MainInformation,
	WidgetContent,
	WidgetContentBlock,
	WidgetHeader,
	WidgetTitle,
} from '../../shared/components/widget/WidgetContainer';
import SecurityWaitTimeInfo from './SecurityWaitTimeInfo';
import { PageRoutes } from '../../app/pages/domain/constants/PageRoutes';
import { useNavigate } from '../../../navigation/useNavigate';
import SecurityIcon from '../../../assets/icons/security.svg';
import { useGetSecurityCheckpointsData } from '../hooks/useGetCheckpointsData';
import { SecurityInformation } from './SecurityInformation';
import SecurityWaitTimesWidgetError from './SecurityWaitTimesWidgetError';
import { Color } from '../../../styles/Color';

export default function SecurityWaitTimesWidget() {
	const navigate = useNavigate();
	const { securityWaitTimes, loading, error } = useGetSecurityCheckpointsData();

	const handleClick = () => {
		navigate(PageRoutes.SecurityWaitTimes);
	};

	if (loading && !error) {
		return <SecurityWaitTimesWidgetShimmer />;
	}

	if (error || !securityWaitTimes) {
		return <SecurityWaitTimesWidgetError onClick={handleClick} />;
	}

	return (
		<Container>
			<MainInformationWrapper>
				<MainInformation>
					<SWTHeader />
					<WidgetContent>
						<SWTMobileHeaders />
						{securityWaitTimes?.map((item: ISecurityCheckpoint) => (
							<SWTItem key={item.name} data={item} />
						))}
					</WidgetContent>
					<WidgetSecurityInformation isWidget />
				</MainInformation>
				<MoreInformationLink />
				<MoreInformationButton onClick={handleClick} />
			</MainInformationWrapper>
			<SWTCardSquircleIcon icon={<SecurityIcon role="presentation" />} />
		</Container>
	);
}

export const WidgetContentMobileHeaders = styled(WidgetContentBlock)`
	display: none;
	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		display: flex;
		height: ${Css.pxToRem(8)};
		> span {
			width: ${Css.pxToRem(94)};
		}
	}
`;

export const SWTMobileHeaders = () => (
	<WidgetContentMobileHeaders>
		<span className="caption">Gates</span>
		<span className="caption">Wait times</span>
	</WidgetContentMobileHeaders>
);

export const SWTHeader = () => (
	<WidgetHeader>
		<WidgetTitle>Security Wait Times</WidgetTitle>
	</WidgetHeader>
);

export const SWTItem = ({ data }: { data: ISecurityCheckpoint }) => (
	<WidgetContentBlock>
		<CheckpointLabel>
			<Flex gap="4">
				<GateLabelDesktop>Gate</GateLabelDesktop> {data.label}
			</Flex>
		</CheckpointLabel>
		<SecurityWaitTimeInfo data={data} />
	</WidgetContentBlock>
);

export const WidgetSecurityInformation = styled(SecurityInformation)`
	display: block;
	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		display: none;
	}
`;
const GateLabelDesktop = styled.span`
	display: block;
	color: ${Color.SecondaryDarkBlue1};
	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		display: none;
	}
`;

export const MoreInformationLink = () => (
	<MoreInfoLink href={PageRoutes.SecurityWaitTimes}>
		<span>More Information</span>{' '}
		<ChevronRightIcon aria-label="More Information" />
	</MoreInfoLink>
);

interface IMoreInformationButtonProps {
	onClick: () => void;
}

export const MoreInformationButton = ({
	onClick,
}: IMoreInformationButtonProps) => (
	<MoreInfoButton buttonType="outlined" onClick={onClick}>
		More Information
	</MoreInfoButton>
);
